import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  CircularProgress,
  ListItem,
  ListItemText,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { Dropzone } from "components/Dropzone";
import { OmnisearchAutocomplete } from "components/OmnisearchAutocomplete";
import { Document, DocumentType } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useToken } from "providers/token";
import { useState } from "react";
import { DOCUMENTS, Row as DocumentRow } from "routes/Customer/Documents";
import invariant from "tiny-invariant";
import { prettyPrintDate } from "util/dates";
import { uploadFile } from "util/uploadFile";

import { Facility, IMPORT_T2S_FILE_MUTATION, T2S_READ_DOCUMENT_QUERY } from "./queries";
import { T2sImportFacilityMapper } from "./T2sImportFacilityMapper";

type T2sImportDialogProps = {
  open: boolean;
  onClose: (event: object, reason: string) => void;
  tenantId: string;
};

export function T2sImportDialog({ open, onClose, tenantId }: T2sImportDialogProps) {
  const { user } = useCurrentUser();
  // New documents need the user
  invariant(user);

  const alerts = useAlerts();

  const defaultSearch = `tenantId:${tenantId}`;

  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | DocumentRow | null>();

  const [mappedFacilities, setMappedFacilities] = useState<(Facility | null | undefined)[]>([]);

  const [disableSubmit, setDisableSubmit] = useState(true);

  const [activeStep, setActiveStep] = useState(0);
  const steps = ["Select T2S File", "Map Facilities"];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const { getIdTokenRefreshIfExpired } = useToken();
  const handleFileSelect = async (files: File[]) => {
    const file = files?.[0];
    if (!file) {
      return;
    }
    try {
      setUploadingFile(true);
      const idToken = await getIdTokenRefreshIfExpired();
      const document = await uploadFile(
        file,
        {
          tenantId,
          userId: user.id,
          description: "T2S file uploaded for import",
          documentType: DocumentType.Tier2Submit,
        },
        idToken
      );
      setSelectedDocument(document);
    } catch (err) {
      alerts.error(`${err}`);
    } finally {
      setUploadingFile(false);
    }
  };

  const { data: t2sFileData } = useQuery(T2S_READ_DOCUMENT_QUERY, {
    variables: {
      documentId: selectedDocument?.id ?? "",
    },
    skip: !selectedDocument?.id,
    onCompleted: () => {
      handleNext();
    },
    onError() {
      alerts.error(`An error occurred while importing the T2S file`);
    },
  });

  const [importT2sFile, { loading: importingT2sFile }] = useMutation(IMPORT_T2S_FILE_MUTATION, {
    onCompleted() {
      alerts.success(`T2S imported successfully`);
    },
    onError() {
      alerts.error(`An error occurred while importing the T2S file`);
    },
    refetchQueries: ["ExtractionsWebAutomationJobs"],
  });

  const handleImport = async () => {
    if (!selectedDocument) {
      alerts.error("No document selected");
      return;
    }
    if (!t2sFileData?.readT2sFile?.reportingYear) {
      alerts.error("No reporting year found in t2s file");
      return;
    }
    await importT2sFile({
      variables: {
        input: {
          documentId: selectedDocument?.id ?? "",
          reportingYear: t2sFileData?.readT2sFile?.reportingYear,
          facilityIds: mappedFacilities.map((f) => f?.id ?? null),
        },
      },
    });
    onClose({}, "success");
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {uploadingFile && (
              <>
                <Typography>Uploading t2s file...</Typography>
                <CircularProgress size={24} />
              </>
            )}
            {selectedDocument && !t2sFileData?.readT2sFile && (
              <>
                <Typography>Reading T2S file...</Typography>
                <CircularProgress size={24} />
              </>
            )}
            {!selectedDocument && !uploadingFile && (
              <>
                <Dropzone
                  label="Drag and drop your document or click to select file"
                  dragActiveText="Drop the file here"
                  accept=".t2s"
                  onDrop={(acceptedFiles) => handleFileSelect(acceptedFiles)}
                />
                <Typography variant="h6">Or select a .t2s file</Typography>
                <OmnisearchAutocomplete
                  runInitialSearch
                  dataQuery={DOCUMENTS}
                  defaultSearch={`${defaultSearch} fileExtension:T2S`}
                  dataQueryVariables={{
                    sort: [{ field: "title", sort: "asc" }],
                  }}
                  getItems={(data) => data.documents.items}
                  onSelectionChange={(selected: DocumentRow | null) => {
                    setSelectedDocument(selected);
                  }}
                  renderOption={(props, item) => (
                    <ListItem key={item.id} {...props} disablePadding>
                      <ListItemText
                        primary={item.title}
                        secondary={`Uploaded: ${prettyPrintDate(item.createdAt)}`}
                      />
                    </ListItem>
                  )}
                />
              </>
            )}
          </Box>
        );
      case 1:
        return (
          <T2sImportFacilityMapper
            t2sFacilities={t2sFileData?.readT2sFile?.facilities || []}
            onComplete={(mappedFacilities) => setMappedFacilities(mappedFacilities)}
            defaultSearch={defaultSearch}
            onProgressChange={(progress) =>
              progress === 100 ? setDisableSubmit(false) : setDisableSubmit(true)
            }
          />
        );
      default:
        return "Unknown step";
    }
  };

  return (
    <Dialog
      title="Import .t2s file"
      fullWidth
      maxWidth="md"
      open={open}
      onClose={onClose}
      primaryActionButtonProps={{
        loading: importingT2sFile,
        onClick: handleImport,
        disabled: disableSubmit || mappedFacilities.every((f) => f === null),
        saveText: "Import",
        hideStartIcon: true,
      }}
    >
      <Stepper activeStep={activeStep}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <Box sx={{ mt: 2 }}>{renderStepContent(activeStep)}</Box>
    </Dialog>
  );
}
